/* @jsxRuntime classic */
/** @jsx createElement */
import {
  BodyThree,
  BodyTwo,
  createElement,
  HeadlineFour,
  HeadlineThree,
  HeadlineTwo,
  OverlineFive,
  OverlineOne,
  OverlineThree,
} from '@nucleus/ncu-section-builder';
import { Height, InfoButtonsLayout, InfoMaxWidth, SectionTypeEnum } from '@nucleus/types/web';
import { Alignment } from '@nucleus/types/web/alignment';
import { Position } from '@nucleus/types/web';
import { SectionDefaultBuilder } from '../SectionDefaultBuilder';
import {
  StandardBlockButtons,
  StandardSectionBackground,
  buildPlaceholderMedia,
  buildStandardWrapper,
} from '../helpers';
import { defaultText } from '../text';

const builder = new SectionDefaultBuilder<SectionTypeEnum.Sermon>()
  .setWrapper(buildStandardWrapper<SectionTypeEnum.Sermon>)
  .addLayout('layout1', ({ lookConfig, sermonEngineId }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-1',
    height: Height.Medium,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        engineId: sermonEngineId,
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
      },
    ],
  }))
  .addLayout('layout2', ({ lookConfig, sermonEngineId }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: false, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    blockButtonsLayout: InfoButtonsLayout.Horizontal,
    blockButtonsWidth: StandardBlockButtons.blockButtonsWidth,
    blocks: [
      {
        engineId: sermonEngineId,
        headline: <HeadlineTwo>{defaultText.headline.short}</HeadlineTwo>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
      },
    ],
  }))
  .addLayout('layout3', ({ lookConfig, sermonEngineId }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-3',
    height: Height.Large,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Small,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        engineId: sermonEngineId,
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
        ],
      },
    ],
  }))
  .addLayout('layout4', ({ sermonEngineId }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockBodyMaxColumns: 2,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        engineId: sermonEngineId,
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: [
          ...BodyTwo({ children: defaultText.body.long }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long2 }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long3 }),
        ],
        actions: [],
        mediaItems: [],
      },
    ],
  }))
  .addLayout('layout5', ({ lookConfig, sermonEngineId }) => ({
    backgroundMedia: { presetMediaKey: 'sectionBackground', presetMediaOrder: 'first' },
    blockBackgroundMedia: {
      useImage: true,
      image: lookConfig?.placeholders?.LightBokeh,
      presetMediaKey: 'blockBackground',
      presetMediaOrder: 'first',
    },
    colorPaletteVariation: 'variation-2',
    height: Height.Large,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Right,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        engineId: sermonEngineId,
        overline: <OverlineOne>{defaultText.overline.standard}</OverlineOne>,
        headline: <HeadlineThree>{defaultText.headline.long}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.short}</BodyTwo>,
        actions: [],
        mediaItems: [],
      },
    ],
  }))
  .addLayout('layout6', ({ lookConfig, sermonEngineId }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-3',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Large,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        engineId: sermonEngineId,
        overline: <OverlineFive>{defaultText.overline.standard}</OverlineFive>,
        headline: <HeadlineFour>{defaultText.headline.short}</HeadlineFour>,
        body: <BodyThree>{defaultText.body.standard}</BodyThree>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
      },
    ],
  }));

export default builder.build();
